import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EventCalendar = ({ events, selectedDate, onDateSelect, currentMonth, onMonthChange }) => {
  const [viewMode, setViewMode] = useState('month'); // month, week

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days?.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateString = date?.toISOString()?.split('T')?.[0];
    return events?.filter(event => event?.date === dateString);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const isSelected = (date) => {
    if (!date || !selectedDate) return false;
    return date?.toDateString() === selectedDate?.toDateString();
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate?.setMonth(newDate?.getMonth() + direction);
    onMonthChange(newDate);
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="bg-card rounded-lg shadow-warm p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-heading font-semibold text-foreground">
            {monthNames?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}
          </h3>
          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateMonth(-1)}
              aria-label="Mês anterior"
            >
              <Icon name="ChevronLeft" size={16} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateMonth(1)}
              aria-label="Próximo mês"
            >
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant={viewMode === 'month' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('month')}
          >
            Mês
          </Button>
          <Button
            variant={viewMode === 'week' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('week')}
          >
            Semana
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays?.map((day) => (
          <div
            key={day}
            className="p-2 text-center text-sm font-medium text-muted-foreground"
          >
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days?.map((date, index) => {
          const dayEvents = getEventsForDate(date);
          const hasEvents = dayEvents?.length > 0;

          return (
            <button
              key={index}
              onClick={() => date && onDateSelect(date)}
              disabled={!date}
              className={`
                relative p-2 h-12 text-sm rounded-lg transition-all duration-200
                ${!date ? 'invisible' : ''}
                ${isToday(date) ? 'bg-primary text-primary-foreground font-semibold' : ''}
                ${isSelected(date) && !isToday(date) ? 'bg-accent text-accent-foreground' : ''}
                ${!isToday(date) && !isSelected(date) ? 'hover:bg-muted text-foreground' : ''}
                ${hasEvents && !isToday(date) && !isSelected(date) ? 'bg-blue-50 text-blue-900' : ''}
              `}
            >
              {date && (
                <>
                  <span className="block">{date?.getDate()}</span>
                  {hasEvents && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                      <div className="flex space-x-0.5">
                        {dayEvents?.slice(0, 3)?.map((_, eventIndex) => (
                          <div
                            key={eventIndex}
                            className={`w-1 h-1 rounded-full ${
                              isToday(date) ? 'bg-primary-foreground' :
                              isSelected(date) ? 'bg-accent-foreground' : 'bg-primary'
                            }`}
                          />
                        ))}
                        {dayEvents?.length > 3 && (
                          <div className="text-xs">+</div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>
      {selectedDate && (
        <div className="mt-4 pt-4 border-t border-border">
          <h4 className="text-sm font-medium text-foreground mb-2">
            Eventos em {selectedDate?.toLocaleDateString('pt-BR')}
          </h4>
          <div className="space-y-2">
            {getEventsForDate(selectedDate)?.map((event) => (
              <div
                key={event?.id}
                className="flex items-center space-x-3 p-2 bg-muted rounded-lg"
              >
                <div className={`w-3 h-3 rounded-full ${
                  event?.type === 'workshop' ? 'bg-blue-500' :
                  event?.type === 'promocao' ? 'bg-green-500' :
                  event?.type === 'evento' ? 'bg-purple-500' :
                  event?.type === 'feira' ? 'bg-orange-500' : 'bg-indigo-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {event?.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {event?.startTime} - {event?.location}
                  </p>
                </div>
              </div>
            ))}
            {getEventsForDate(selectedDate)?.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Nenhum evento agendado para esta data.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventCalendar;